/**
 * Attributes tab (BON-161) — per-category attribute definitions with add +
 * soft-disable. Adding a required attribute that existing items violate prompts
 * a forceCreate confirmation.
 */
import { Plus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import {
  CATALOG_CATEGORIES,
  fetchAttributeDefinitions,
  disableAttributeDefinition,
  type AttributeDefinition,
  type CatalogCategory,
} from '../../../api/inventory-catalog';

import AddAttributeModal from './AddAttributeModal';

function DefRow({
  d,
  canEdit,
  onDisable,
}: {
  d: AttributeDefinition;
  canEdit: boolean;
  onDisable: () => void;
}) {
  return (
    <li className="flex items-center gap-3 px-4 py-2.5 text-sm">
      <span className="font-medium text-stone-900 dark:text-stone-100">{d.name}</span>
      <span className="text-xs text-stone-400">{d.dataType}</span>
      {d.isRequired && <span className="text-[10px] uppercase text-moss-600">required</span>}
      {!d.isActive && <span className="text-[10px] uppercase text-stone-400">disabled</span>}
      {canEdit && d.isActive && (
        <button onClick={onDisable} className="ml-auto text-xs text-red-600 hover:underline">
          Disable
        </button>
      )}
    </li>
  );
}

export default function AttributeDefinitionsTab({ canEdit }: { canEdit: boolean }) {
  const [category, setCategory] = useState<CatalogCategory>(CATALOG_CATEGORIES[0]);
  const [defs, setDefs] = useState<AttributeDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    fetchAttributeDefinitions(category)
      .then(setDefs)
      .catch(() => setDefs([]))
      .finally(() => setLoading(false));
  }, [category]);

  useEffect(load, [load]);

  async function handleDisable(id: string) {
    await disableAttributeDefinition(id);
    load();
  }

  return (
    <div className="bg-white dark:bg-[#1e2521] rounded-xl border border-stone-200 dark:border-stone-700">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-stone-200 dark:border-stone-700">
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as (typeof CATALOG_CATEGORIES)[number])}
          className="rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-[#242d28] px-2 py-1.5 text-sm"
        >
          {CATALOG_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <span className="ml-auto text-xs text-stone-400">
          {defs.filter((d) => d.isActive).length} active
        </span>
        {canEdit && (
          <button
            onClick={() => setAdding(true)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-moss-600 text-white hover:bg-moss-700 transition"
          >
            <Plus size={14} /> Add attribute
          </button>
        )}
      </div>

      <ul className="divide-y divide-stone-100 dark:divide-stone-700/50">
        {loading ? (
          <li className="py-8 text-center text-stone-500 text-sm">Loading…</li>
        ) : defs.length === 0 ? (
          <li className="py-8 text-center text-stone-500 text-sm">No attributes for {category}</li>
        ) : (
          defs.map((d) => (
            <DefRow key={d.id} d={d} canEdit={canEdit} onDisable={() => handleDisable(d.id)} />
          ))
        )}
      </ul>

      {adding && (
        <AddAttributeModal
          category={category}
          onClose={() => setAdding(false)}
          onAdded={() => {
            setAdding(false);
            load();
          }}
        />
      )}
    </div>
  );
}
